import i18next from "i18next";
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";

export function LeagueSeasonComparison({
  data,
}) {
  const [statisticsVisible, setStatisticsVisible] =
    useState(true);

  const [outcomeDisplay, setOutcomeDisplay] =
    useState("percent");

  const [goalDisplay, setGoalDisplay] =
    useState("average");

  if (!data) {
    return null;
  }

  function numberText(
    value,
    digits = 1
  ) {
    if (value === null || value === undefined || value === "") return "–";
    const number =
      Number(value);

    return Number.isFinite(number)
      ? number.toFixed(digits)
      : "–";
  }

  function percentText(
    value
  ) {
    if (value === null || value === undefined || value === "") return "–";
    const number =
      Number(value);

    return Number.isFinite(number)
      ? `${(number * 100).toFixed(1)} %`
      : "–";
  }

  function aggregateTable(
    table,
    prefix
  ) {
    const rows =
      Array.isArray(table)
        ? table
        : [];

    const read = (
      row,
      location,
      field
    ) => {
      const number =
        Number(
          row
            ?.[location]
            ?.[`${prefix}_${field}`]
        );

      return Number.isFinite(number)
        ? number
        : 0;
    };

    return rows.reduce(
      (summary, row) => {
        summary.fixtures +=
          read(
            row,
            "home",
            "games_played"
          );

        summary.homeWins +=
          read(
            row,
            "home",
            "won"
          );

        summary.draws +=
          read(
            row,
            "home",
            "draw"
          );

        summary.awayWins +=
          read(
            row,
            "home",
            "lost"
          );

        summary.homeGoals +=
          read(
            row,
            "home",
            "goals_scored"
          );

        summary.awayGoals +=
          read(
            row,
            "away",
            "goals_scored"
          );

        return summary;
      },
      {
        fixtures: 0,
        homeWins: 0,
        draws: 0,
        awayWins: 0,
        homeGoals: 0,
        awayGoals: 0,
      }
    );
  }

  const actualSummary =
    data?.season_base_summary;

  const actual =
    actualSummary
      ? {
          key: "actual",
          label:
            i18next.t(
              "help.leagueSeasonComparison.actual"
            ),
          fixtures:
            Number(
              actualSummary
                .fixtures_played
            ) || 0,
          homeWins:
            Number(
              actualSummary
                .home_wins
            ) || 0,
          draws:
            Number(
              actualSummary
                .draws
            ) || 0,
          awayWins:
            Number(
              actualSummary
                .away_wins
            ) || 0,
          homeGoals:
            Number(
              actualSummary
                .home_goals
            ) || 0,
          awayGoals:
            Number(
              actualSummary
                .away_goals
            ) || 0,
        }
      : null;

  const expectedTable =
    data
      ?.record
      ?.expected_table;

  const expected =
    Array.isArray(expectedTable) && expectedTable.length > 0
      ? {
          key: "expected",
          label:
            i18next.t(
              "help.leagueSeasonComparison.expected"
            ),
          ...aggregateTable(
            expectedTable,
            "expected"
          ),
        }
      : null;

  const shouldTable =
    data
      ?.record
      ?.should_table;

  const should =
    Array.isArray(shouldTable) && shouldTable.length > 0
      ? {
          key: "should",
          label: "Should",
          ...aggregateTable(
            shouldTable,
            "should"
          ),
        }
      : null;

  const rows = [
    actual,
    expected,
    should,
  ].filter(Boolean);

  function outcomeValue(
    count,
    fixtures
  ) {
    const countNumber =
      Number(count);

    const fixtureNumber =
      Number(fixtures);

    if (
      !Number.isFinite(
        countNumber
      ) ||
      !Number.isFinite(
        fixtureNumber
      ) ||
      fixtureNumber <= 0
    ) {
      return "–";
    }

    if (
      outcomeDisplay ===
      "total"
    ) {
      return countNumber.toFixed(
        Number.isInteger(
          countNumber
        )
          ? 0
          : 1
      );
    }

    const probability =
      countNumber /
      fixtureNumber;

    if (
      outcomeDisplay ===
      "odds"
    ) {
      return probability > 0
        ? (
            1 /
            probability
          ).toFixed(2)
        : "–";
    }

    return `${(
      probability *
      100
    ).toFixed(1)} %`;
  }

  function goalAverage(
    row
  ) {
    const fixtures =
      Number(row?.fixtures);

    const total =
      Number(row?.homeGoals) +
      Number(row?.awayGoals);

    return (
      Number.isFinite(fixtures) &&
      fixtures > 0 &&
      Number.isFinite(total)
    )
      ? total / fixtures
      : null;
  }

  function homeAdvantage(
    row
  ) {
    const home =
      Number(row?.homeGoals);

    const away =
      Number(row?.awayGoals);

    const total =
      home +
      away;

    return (
      Number.isFinite(home) &&
      Number.isFinite(total) &&
      total > 0
    )
      ? home /
        (total / 2)
      : null;
  }

  function goalValues(
    row
  ) {
    const fixtures =
      Number(row?.fixtures);

    const home =
      Number(row?.homeGoals);

    const away =
      Number(row?.awayGoals);

    const total =
      home +
      away;

    if (
      !Number.isFinite(
        fixtures
      ) ||
      fixtures <= 0 ||
      !Number.isFinite(home) ||
      !Number.isFinite(away)
    ) {
      return {
        home: "–",
        away: "–",
        total: "–",
      };
    }

    if (
      goalDisplay ===
      "total"
    ) {
      return {
        home:
          numberText(
            home,
            Number.isInteger(home)
              ? 0
              : 1
          ),
        away:
          numberText(
            away,
            Number.isInteger(away)
              ? 0
              : 1
          ),
        total:
          numberText(
            total,
            Number.isInteger(total)
              ? 0
              : 1
          ),
      };
    }

    if (
      goalDisplay ===
      "distribution"
    ) {
      return {
        home:
          total > 0
            ? percentText(
                home /
                total
              )
            : "–",
        away:
          total > 0
            ? percentText(
                away /
                total
              )
            : "–",
        total:
          total > 0
            ? "100 %"
            : "–",
      };
    }

    return {
      home:
        numberText(
          home /
          fixtures,
          2
        ),
      away:
        numberText(
          away /
          fixtures,
          2
        ),
      total:
        numberText(
          total /
          fixtures,
          2
        ),
    };
  }

  const seasonParameters =
    data?.season_parameters ||
    {};

  const modelStatistics =
    data
      ?.record
      ?.model_statistics ||
    {};

  const comparisonColumns = [
    {
      key: "model",
      label:
        i18next.t(
          "help.leagueSeasonComparison.modelParameters"
        ),
    },
    {
      key: "actual",
      label: "Real",
    },
    {
      key: "expected",
      label: "Exp",
    },
    {
      key: "should",
      label: "Should",
    },
  ];

  const coreRows = [
    {
      key: "goal-average",
      label:
        i18next.t(
          "help.leagueSeasonComparison.goalAverage"
        ),
      tooltip:
        i18next.t(
          "help.leagueSeasonComparison.goalAverageTooltip"
        ),
      values: {
        model:
          numberText(
            seasonParameters
              ?.stored_goal_average,
            2
          ),
        actual:
          actual
            ? numberText(
                goalAverage(
                  actual
                ),
                2
              )
            : "–",
        expected:
          expected
            ? numberText(
                goalAverage(
                  expected
                ),
                2
              )
            : "–",
        should:
          should
            ? numberText(
                goalAverage(
                  should
                ),
                2
              )
            : "–",
      },
    },
    {
      key: "home-advantage",
      label:
        i18next.t(
          "help.leagueSeasonComparison.homeAdvantage"
        ),
      tooltip:
        i18next.t(
          "help.leagueSeasonComparison.homeAdvantageTooltip"
        ),
      values: {
        model:
          numberText(
            seasonParameters
              ?.exp_home_factor,
            3
          ),
        actual:
          actual
            ? numberText(
                homeAdvantage(
                  actual
                ),
                3
              )
            : "–",
        expected:
          expected
            ? numberText(
                homeAdvantage(
                  expected
                ),
                3
              )
            : "–",
        should:
          should
            ? numberText(
                homeAdvantage(
                  should
                ),
                3
              )
            : "–",
      },
    },
    {
      key: "draw",
      label:
        i18next.t(
          "help.leagueSeasonComparison.drawFactorAndRate"
        ),
      tooltip:
        i18next.t(
          "help.leagueSeasonComparison.drawFactorTooltip"
        ),
      values: {
        model:
          numberText(
            seasonParameters
              ?.drawfactor,
            3
          ),
        actual:
          actual
            ? outcomeValue(
                actual.draws,
                actual.fixtures
              )
            : "–",
        expected:
          expected
            ? outcomeValue(
                expected.draws,
                expected.fixtures
              )
            : "–",
        should:
          should
            ? outcomeValue(
                should.draws,
                should.fixtures
              )
            : "–",
      },
    },
    {
      key: "probability",
      label:
        i18next.t(
          "help.leagueSeasonComparison.averageProbability"
        ),
      tooltip:
        i18next.t(
          "help.leagueSeasonComparison.averageProbabilityTooltip"
        ),
      values: {
        model: "–",
        actual:
          percentText(
            modelStatistics
              ?.average_occurred_probability
          ),
        expected:
          percentText(
            modelStatistics
              ?.average_expected_probability
          ),
        should: "–",
      },
    },
    {
      key: "goal-deviation",
      label:
        i18next.t(
          "help.leagueSeasonComparison.averageGoalDeviation"
        ),
      tooltip:
        i18next.t(
          "help.leagueSeasonComparison.goalDeviationTooltip"
        ),
      calculate: true,
      values: {
        model: "–",
        actual:
          numberText(
            modelStatistics
              ?.average_occurred_goal_deviation,
            3
          ),
        expected:
          numberText(
            modelStatistics
              ?.average_expected_goal_deviation,
            3
          ),
        should: "–",
      },
    },
  ];

  return (
    <Box
      sx={{
        minWidth: 0,
        color: "#17242d",
      }}
    >
      {!statisticsVisible ? (
        <Box
          sx={{
            gridColumn: {
              xs: "1",
              lg: "1",
            },
            gridRow: {
              xs: "auto",
              lg: "1",
            },
            minHeight: 28,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setStatisticsVisible(
                true
              )
            }
            sx={{
              px: 1,
              py: 0.3,
              textTransform: "none",
              fontSize: "0.72rem",
              fontWeight: 800,
            }}
          >
            {i18next.t(
              "help.leagueSeasonComparison.showStatistics"
            )}
          </Button>
        </Box>
      ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
              "minmax(0, 1fr)",
            alignContent: "start",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              gridColumn: {
                xs: "1",
                lg: "1",
              },
              gridRow: {
                xs: "auto",
                lg: "1",
              },
              display: "flex",
              alignItems: "center",
              justifyContent:
                "space-between",
              gap: 1,
              mb: 0.1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
              }}
            >
              {i18next.t(
                "help.leagueSeasonComparison.seasonComparison"
              )}
            </Typography>

            <Button
              size="small"
              onClick={() =>
                setStatisticsVisible(
                  false
                )
              }
              sx={{
                minWidth: 0,
                px: 0.6,
                py: 0.15,
                textTransform: "none",
                fontSize: "0.66rem",
                color:
                  "text.secondary",
              }}
            >
              {i18next.t(
                "help.leagueSeasonComparison.hideStatistics"
              )}
            </Button>
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1",
                lg: "1",
              },
              gridRow: {
                xs: "auto",
                lg: "2",
              },
              display: "grid",
              gridTemplateColumns:
                "minmax(210px, 1.6fr) repeat(4, minmax(105px, 1fr))",
              width: "100%",
              minWidth: 680,
              alignItems: "center",
              mt: 0.8,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              overflow: "hidden",
              bgcolor: "white",
              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            <Typography
              component="span"
              sx={{
                px: 0.6,
                py: 0.25,
                borderBottom:
                  "1px solid",
                borderColor:
                  "divider",
                fontSize:
                  "0.65rem",
                color:
                  "rgba(255,255,255,.76)",
                fontWeight: 900,
                bgcolor: "#25343e",
              }}
            >
              {i18next.t(
                "help.leagueSeasonComparison.metric"
              )}
            </Typography>

            {comparisonColumns.map(
              (column) => (
                <Typography
                  key={
                    column.key
                  }
                  component="span"
                  align="right"
                  sx={{
                    px: 0.6,
                    py: 0.25,
                    borderBottom:
                      "1px solid",
                    borderColor:
                      "divider",
                    fontSize:
                      "0.65rem",
                    color:
                      "rgba(255,255,255,.76)",
                    fontWeight: 900,
                    bgcolor: "#25343e",
                  }}
                >
                  {column.label}
                </Typography>
              )
            )}

            {coreRows.flatMap(
              (row) => {
                const labelCell = (
                  <Box
                    key={`${row.key}-label`}
                    title={
                      row.tooltip
                    }
                    sx={{
                      minWidth: 0,
                      minHeight: 21,
                      px: 0.45,
                      py: 0.12,
                      display: "flex",
                      alignItems:
                        "center",
                      gap: 0.5,
                      borderBottom:
                        "1px solid",
                      borderColor:
                        "divider",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        minWidth: 0,
                        fontSize:
                          "0.75rem",
                        fontWeight:
                          700,
                        lineHeight: 1.25,
                      }}
                    >
                      {row.label}
                    </Typography>

                    {row.calculate && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled
                        title={i18next.t(
                          "help.leagueSeasonComparison.calculationComing"
                        )}
                        aria-label={i18next.t(
                          "help.leagueSeasonComparison.calculateGoalDeviation"
                        )}
                        sx={{
                          ml: "auto",
                          minWidth: 28,
                          height: 20,
                          px: 0.45,
                          py: 0,
                          fontSize:
                            "0.62rem",
                          textTransform:
                            "none",
                        }}
                      >
                        Calc
                      </Button>
                    )}
                  </Box>
                );

                const valueCells =
                  comparisonColumns.map(
                    (column) => (
                      <Typography
                        key={`${row.key}-${column.key}`}
                        component="span"
                        align="right"
                        sx={{
                          minHeight: 21,
                          px: 0.45,
                          py: 0.18,
                          borderBottom:
                            "1px solid",
                          borderColor:
                            "divider",
                          fontSize:
                            "0.69rem",
                          fontWeight:
                            800,
                        }}
                      >
                        {
                          row.values[
                            column.key
                          ]
                        }
                      </Typography>
                    )
                  );

                return [
                  labelCell,
                  ...valueCells,
                ];
              }
            )}
          </Box>

          <Box
            sx={{
              gridColumn:
                "1 / -1",
              gridRow: {
                xs: "auto",
                lg: "3",
              },
              mt: 0.35,
              pt: 1.4,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.35,
            }}
          >
            <Typography component="span" sx={{ width: "100%", mb: 0.45, fontSize: "1rem", fontWeight: 900 }}>
              1X2- und Toranalyse
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize:
                  "0.65rem",
                fontWeight:
                  900,
              }}
            >
              1X2
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={
                outcomeDisplay
              }
              onChange={(
                event,
                value
              ) => {
                if (value) {
                  setOutcomeDisplay(
                    value
                  );
                }
              }}
            >
              <ToggleButton
                value="total"
                sx={{
                  px: 0.6,
                  py: 0,
                  fontSize:
                    "0.62rem",
                  textTransform:
                    "none",
                }}
              >
                {i18next.t(
                  "help.leagueSeasonComparison.totals"
                )}
              </ToggleButton>

              <ToggleButton
                value="percent"
                sx={{
                  px: 0.6,
                  py: 0,
                  fontSize:
                    "0.62rem",
                  textTransform:
                    "none",
                }}
              >
                %
              </ToggleButton>

              <ToggleButton
                value="odds"
                sx={{
                  px: 0.6,
                  py: 0,
                  fontSize:
                    "0.62rem",
                  textTransform:
                    "none",
                }}
              >
                {i18next.t(
                  "help.leagueSeasonComparison.fairOdds"
                )}
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography
              component="span"
              sx={{
                ml: 0.7,
                fontSize:
                  "0.65rem",
                fontWeight:
                  900,
              }}
            >
              {i18next.t(
                "help.leagueSeasonComparison.goals"
              )}
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={
                goalDisplay
              }
              onChange={(
                event,
                value
              ) => {
                if (value) {
                  setGoalDisplay(
                    value
                  );
                }
              }}
            >
              <ToggleButton
                value="total"
                sx={{
                  px: 0.6,
                  py: 0,
                  fontSize:
                    "0.62rem",
                  textTransform:
                    "none",
                }}
              >
                {i18next.t(
                  "help.leagueSeasonComparison.totals"
                )}
              </ToggleButton>

              <ToggleButton
                value="average"
                sx={{
                  px: 0.6,
                  py: 0,
                  fontSize:
                    "0.62rem",
                  textTransform:
                    "none",
                }}
              >
                {i18next.t(
                  "help.leagueSeasonComparison.average"
                )}
              </ToggleButton>

              <ToggleButton
                value="distribution"
                sx={{
                  px: 0.6,
                  py: 0,
                  fontSize:
                    "0.62rem",
                  textTransform:
                    "none",
                }}
              >
                %
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              gridColumn:
                "1 / -1",
              gridRow: {
                xs: "auto",
                lg: "4",
              },
              display: "grid",
              gridTemplateColumns:
                "minmax(120px, 1fr) 58px repeat(3, minmax(78px, 1fr)) repeat(3, minmax(84px, 1fr))",
              width: "100%",
              minWidth: 760,
              alignItems: "center",
              mt: 0.7,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              overflow: "hidden",
              bgcolor: "white",
              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            {[
              i18next.t(
                "help.leagueSeasonComparison.comparison"
              ),
              i18next.t(
                "help.leagueSeasonComparison.matchesShort"
              ),
              i18next.t(
                "help.leagueSeasonComparison.homeWin"
              ),
              i18next.t(
                "help.leagueSeasonComparison.draw"
              ),
              i18next.t(
                "help.leagueSeasonComparison.awayWin"
              ),
              i18next.t(
                "help.leagueSeasonComparison.homeGoals"
              ),
              i18next.t(
                "help.leagueSeasonComparison.awayGoals"
              ),
              i18next.t(
                "help.leagueSeasonComparison.totalGoals"
              ),
            ].map(
              (
                label,
                index
              ) => (
                <Typography
                  key={`comparison-heading-${index}`}
                  component="span"
                  align={
                    index > 0
                      ? "right"
                      : "left"
                  }
                  sx={{
                    px: 0.45,
                    py: 0.22,
                    borderBottom:
                      "1px solid",
                    borderColor:
                      "divider",
                    fontSize:
                      "0.61rem",
                    color:
                      "#44525c",
                    fontWeight:
                      900,
                    bgcolor: "#edf1f4",
                  }}
                >
                  {label}
                </Typography>
              )
            )}

            {rows.flatMap(
              (row) => {
                const goals =
                  goalValues(
                    row
                  );

                const values = [
                  row.label,
                  numberText(
                    row.fixtures,
                    Number.isInteger(
                      row.fixtures
                    )
                      ? 0
                      : 1
                  ),
                  outcomeValue(
                    row.homeWins,
                    row.fixtures
                  ),
                  outcomeValue(
                    row.draws,
                    row.fixtures
                  ),
                  outcomeValue(
                    row.awayWins,
                    row.fixtures
                  ),
                  goals.home,
                  goals.away,
                  goals.total,
                ];

                return values.map(
                  (
                    value,
                    index
                  ) => (
                    <Typography
                      key={`${row.key}-${index}`}
                      component="span"
                      align={
                        index > 0
                          ? "right"
                          : "left"
                      }
                      title={
                        row.key ===
                        "should"
                          ? i18next.t(
                              "help.leagueSeasonComparison.shouldTooltip"
                            )
                          : undefined
                      }
                      sx={{
                        px: 0.45,
                        py: 0.25,
                        borderBottom:
                          "1px solid",
                        borderColor:
                          "divider",
                        fontSize:
                          "0.65rem",
                        fontWeight:
                          index === 0
                            ? 900
                            : 700,
                        bgcolor:
                          row.key ===
                          "should"
                            ? "rgba(126, 53, 88, 0.055)"
                            : "transparent",
                      }}
                    >
                      {value}
                    </Typography>
                  )
                );
              }
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
